import { Box, Button, Dialog, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Step, StepContent, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import { API } from 'aws-amplify';
import React, { useState, useEffect } from 'react';
import { logToDatadog } from 'services/datadog';

interface AssetCheckDialogProps {
  assetId: number;
}

interface CheckVersion {
  id: number;
}

const AssetCheckDialog = React.forwardRef((props: AssetCheckDialogProps, ref) => {
  const [openStateModal, setOpenStateModal] = useState(false);
  const handleOpenModal = () => {
    setOpenStateModal(true);
  };
  const handleCloseModal = () => setOpenStateModal(false);
  const [checks, setChecks] = useState([]);
  const [checkVersions, setCheckVersions] = useState([]);
  const [checkResults, setCheckResults] = useState([]);
  const [checkItems, setCheckItems] = useState([]);
  const [selectedCheck, setSelectedCheck] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<CheckVersion>(null);
  const [note, setNote] = useState('');

  function onCreateButtonClicked() {
    const checkSubmission = {
      checkResults: checkResults.map(result => ({
        checkResult: {
          grade: parseInt(result.grade),
          checkItemId: result.stepId,
          comment: result.comment
        },
        issues: [] 
      })),
      note: note,
      check: selectedCheck.split('-')[1],
      checkId: selectedCheck.split('-')[0]
    };

    API.post('bom', `/check/result/${props.assetId}`, {
      body: checkSubmission,
      queryStringParameters: { bypass: false }
    })
    .then(response => {
      handleCloseModal();
    })
    .catch(error => {
      logToDatadog('error_submit_check', {error}, 'error');
      alert(error.response.data.message);
    });
  }

  const [activeStep, setActiveStep] = useState(0);
  const [grade, setGrade] = useState('');
  const [comment, setComment] = useState('');

  const handleNext = () => {
    const updatedResults = [...checkResults];
    updatedResults[activeStep] = {
      stepId: checkItems[activeStep].id,
      grade: grade,
      comment: comment,
    };
    setCheckResults(updatedResults);
    setGrade('');
    setComment('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setGrade(checkResults[activeStep - 1]?.grade || '');
      setComment(checkResults[activeStep - 1]?.comment || '');
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCheckResults([]);
  };

  useEffect(() => {
    if (openStateModal) {
      API.get('bom', `/check/${props.assetId}`, {})
        .then(p => {
          setChecks(p);
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [openStateModal]);

  useEffect(() => {
    if (openStateModal) {
      const checkVersionId = selectedCheck.split('-')[0];
      API.get('bom', `/check/version/${checkVersionId}`, {})
        .then(p => {
          setCheckVersions(p);
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [selectedCheck]);

  useEffect(() => {
    if (openStateModal) {
      setSelectedVersion(checkVersions[0]);
    }
  }, [checkVersions]);
  

  useEffect(() => {
    if (openStateModal) {
      API.get('bom', `/check/item/${selectedVersion.id}`, {})
        .then(p => {
          setCheckItems(p);
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [selectedVersion]);

  return (
    <>
      <Button onClick={handleOpenModal} color="secondary" size="small" variant="contained">Check</Button>
      <Dialog
        onClose={handleCloseModal}
        open={openStateModal}
        BackdropProps={{ style: { backgroundColor: "transparent" } }}
        PaperProps={{
          style: {
            margin: '10px 0px',
            width: '100%',
            padding: '20px',
            maxWidth: 1000,
          },
        }}
        classes={{ scrollPaper: 'align-content-normal align-items-normal' }}
        style={{ height: '90%', maxHeight: 700, margin: 0 }}
      >
        <DialogTitle>Check</DialogTitle>
        <FormControl fullWidth margin="normal">
          <InputLabel id="check-type-select-label">Check</InputLabel>
          <Select
            labelId="check-type-select-label"
            value={selectedCheck}
            label="Check"
            onChange={e => {
              console.log(e.target.value);
              setSelectedCheck(e.target.value);
            }}
          >
            {checks.map(check => (
              <MenuItem key={check.id} value={check.id + '-' + check.name}>
                {check.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="check-type-select-label">Version</InputLabel>
          <Select
            labelId="check-type-select-label"
            value={selectedVersion ? JSON.stringify(selectedVersion) : ""}
            label="Version"
            onChange={e => setSelectedVersion(JSON.parse(e.target.value))}
          >
            {checkVersions.map(version => (
              <MenuItem key={version.id} value={JSON.stringify(version)}>
                {`Version ${version.version}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div style={{ padding: 12 }}>
          <Box sx={{ maxWidth: 400 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {checkItems.map((item, index) => (
                <Step key={item.id}>
                  <StepLabel>{item.name}</StepLabel>
                  <StepContent>
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby="row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={grade}
                        onChange={e => setGrade(e.target.value)}
                      >
                        <FormControlLabel value="1" control={<Radio color="success" />} label="Grade 1" />
                        <FormControlLabel value="2" control={<Radio color="success" />} label="Grade 2" />
                        <FormControlLabel value="3" control={<Radio color="success" />} label="Grade 3" />
                      </RadioGroup>
                    </FormControl>
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                      <TextField
                        id="comment-field"
                        label="Comment"
                        variant="outlined"
                        sx={{ mr: 2, flexGrow: 1 }}
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mt: 1, mr: 1 }}
                          disabled={!grade} 
                        >
                          {index === checkItems.length - 1 ? 'Finish' : 'Continue'}
                        </Button>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === checkItems.length && (
              <Paper square elevation={0} sx={{ p: 3 }}>
                <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                  Reset
                </Button>
              </Paper>
            )}
          </Box>
        </div>
        <div style={{ padding: 12 }}>
          <Button onClick={onCreateButtonClicked} color="secondary" size="small" variant="contained">
            Submit
          </Button>
        </div>
      </Dialog>
    </>
  );
});

export default AssetCheckDialog;
